import React from 'react';
import { useSelectedSongsStore } from '@/store/selectedSongsStore';
import { Save, Music, Upload, Image as ImageIcon, Trash2, Tag, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import axios from '@/lib/axios';

interface TaxonomyItem {
  _id: string;
  name: string;
  color?: string;
}

interface PlaylistFormProps {
  onSuccess?: () => void;
  isAi?: boolean;
}

interface PlaylistFormData {
  name: string;
  description: string;
  songs: string[];
  artwork?: File;
}

export const PlaylistForm: React.FC<PlaylistFormProps> = ({ onSuccess, isAi = false }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { selectedSongs, clearSelection, removeSong } = useSelectedSongsStore();
  const [formData, setFormData] = React.useState<PlaylistFormData>({
    name: '',
    description: '',
    songs: []
  });
  const [artworkPreview, setArtworkPreview] = React.useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Taksonomi state (sadece AI playlist için)
  const [categories, setCategories] = React.useState<TaxonomyItem[]>([]);
  const [moods, setMoods] = React.useState<TaxonomyItem[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<string>('');
  const [selectedMoods, setSelectedMoods] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!isAi) return;
    (async () => {
      try {
        const [catRes, moodRes] = await Promise.all([
          axios.get('/categories'),
          axios.get('/moods')
        ]);
        setCategories(catRes.data || []);
        setMoods(moodRes.data || []);
      } catch (err) {
        console.error('Taxonomy fetch error:', err);
      }
    })();
  }, [isAi]);

  const toggleMood = (moodId: string) => {
    setSelectedMoods(prev =>
      prev.includes(moodId) ? prev.filter(id => id !== moodId) : [...prev, moodId]
    );
  };

  React.useEffect(() => {
    if (selectedSongs.length > 0) {
      setFormData(prev => ({
        ...prev,
        songs: selectedSongs.map(song => song._id)
      }));
    }
  }, [selectedSongs]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleArtworkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          variant: "destructive",
          title: "Hata",
          description: "Artwork dosyası 5MB'dan küçük olmalıdır",
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: "Hata",
          description: "Lütfen geçerli bir resim dosyası seçin",
        });
        return;
      }

      setFormData(prev => ({ ...prev, artwork: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setArtworkPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedSongs.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen en az bir şarkı seçin",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      selectedSongs.forEach(song => {
        formDataToSend.append('songs[]', song._id);
      });
      if (formData.artwork) {
        formDataToSend.append('artwork', formData.artwork);
      }
      formDataToSend.append('createdBy', 'system');
      if (isAi) {
        formDataToSend.append('isAi', 'true');
        if (selectedCategory) {
          formDataToSend.append('category', selectedCategory);
        }
        selectedMoods.forEach(moodId => {
          formDataToSend.append('moods[]', moodId);
        });
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/playlists`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error("Playlist oluşturulamadı");
      }

      toast({
        title: "Başarılı",
        description: "Playlist başarıyla oluşturuldu",
      });

      clearSelection();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Playlist oluşturulurken bir hata oluştu",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveSong = (songId: string) => {
    removeSong(songId);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
      <div className="grid md:grid-cols-[300px,1fr] gap-8">
        {/* Sol Kolon - Artwork ve Detaylar */}
        <div className="space-y-6">
          {/* Artwork Upload */}
          <div 
            className="relative aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors cursor-pointer overflow-hidden"
            onClick={() => fileInputRef.current?.click()}
          >
            {artworkPreview ? (
              <div className="relative group">
                <img 
                  src={artworkPreview} 
                  alt="Playlist artwork" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Upload className="h-8 w-8 text-white" />
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground/50">
                <ImageIcon className="h-12 w-12 mb-2" />
                <p className="text-sm font-medium">Artwork Yükle</p>
                <p className="text-xs">PNG, JPG veya GIF (max 5MB)</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleArtworkChange}
              className="hidden"
            />
          </div>

          {/* Form Alanları */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Playlist Adı
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border bg-background"
                placeholder="Yeni Playlist"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                Açıklama
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border bg-background min-h-[100px] resize-none"
                placeholder="Playlist açıklaması..."
              />
            </div>

            {/* AI playlist taksonomi seçimi */}
            {isAi && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1.5">
                    <Tag className="h-4 w-4 text-indigo-600" />
                    Kategori
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border bg-background"
                  >
                    <option value="">Kategori seçin...</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {categories.length === 0 && (
                    <p className="text-xs text-slate-500">
                      Henüz kategori yok. Taksonomi sayfasından ekleyebilirsin.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    Mood'lar
                    {selectedMoods.length > 0 && (
                      <span className="text-xs text-slate-500">({selectedMoods.length} seçili)</span>
                    )}
                  </label>
                  {moods.length === 0 ? (
                    <p className="text-xs text-slate-500">
                      Henüz mood yok. Taksonomi sayfasından ekleyebilirsin.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {moods.map(mood => {
                        const isSelected = selectedMoods.includes(mood._id);
                        const color = mood.color || '#a855f7';
                        return (
                          <button
                            key={mood._id}
                            type="button"
                            onClick={() => toggleMood(mood._id)}
                            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all border"
                            style={
                              isSelected
                                ? { backgroundColor: color, borderColor: color, color: '#fff' }
                                : { backgroundColor: 'transparent', borderColor: color + '60', color }
                            }
                          >
                            {mood.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sağ Kolon - Seçili Şarkılar */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Seçili Şarkılar ({selectedSongs.length})</h3>
            {selectedSongs.length > 0 && (
              <button
                type="button"
                onClick={() => clearSelection()}
                className="text-sm text-muted-foreground hover:text-destructive transition-colors"
              >
                Tümünü Kaldır
              </button>
            )}
          </div>

          {selectedSongs.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <Music className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">Henüz şarkı seçilmedi</p>
              <p className="text-xs text-muted-foreground mt-1">Müzik sayfasından şarkı seçebilirsiniz</p>
            </div>
          ) : (
            <div className="border rounded-lg divide-y overflow-hidden bg-card">
              {selectedSongs.map((song) => (
                <div key={song._id} className="p-3 flex items-center justify-between group hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Music className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{song.name || song.originalName}</p>
                      <p className="text-sm text-muted-foreground truncate">{song.artist || 'Bilinmeyen Sanatçı'}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSong(song._id)}
                    className="p-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <button
          type="submit"
          disabled={isSubmitting || formData.name.trim() === '' || selectedSongs.length === 0}
          className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-md flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <Save className="h-5 w-5" />
          {isSubmitting ? 'Oluşturuluyor...' : 'Playlist Oluştur'}
        </button>
      </div>
    </form>
  );
};
